import { VmBottomSheet } from 'Components/vm-bottom-sheet/vm-bottom-sheet';
import { customElement } from 'lit/decorators.js';

const OUTER_WRAP_SELECTOR = '.video-js__outer-wrap';
const VIDEO_WRAP_SELECTOR = '.video-js__video-wrap';
const ACTIONS_WRAP_SELECTOR = '.video-js__actions-wrap';

/**
 * Below this there's no useful room left under the video (e.g. mobile landscape, where
 * the player is taller than the viewport), so the sheet takes the whole viewport instead.
 */
const MIN_SHEET_HEIGHT = 200;

/** `Element.closest` that also climbs out of shadow roots, as the sheet usually renders inside its consumer's. */
function closestComposed(element: Element, selector: string): Element | null {
    let node: Element | null = element;
    while (node) {
        const match = node.closest(selector);
        if (match) return match;
        const root = node.getRootNode();
        node = root instanceof ShadowRoot ? root.host : null;
    }
    return null;
}

/**
 * A `<vm-bottom-sheet>` docked under the video player: opening it scrolls the video to the
 * top of the viewport and sizes the sheet to reach up to the actions row below it, so the
 * video stays visible and playable while the sheet is open.
 *
 * Pair with `disable-backdrop` so the video underneath stays interactive:
 *
 * ```ts
 * html`<vm-video-autosize-bottom-sheet
 *     disable-backdrop
 *     .open=${this._sheetOpen}
 *     @vm-close=${() => (this._sheetOpen = false)}
 * ></vm-video-autosize-bottom-sheet>`;
 * ```
 *
 * The sheet sizes itself, so `initialHeight` and `fullscreen` are overwritten on open.
 *
 * Relies on the video player template's wraps. Elsewhere (no player wraps around the sheet)
 * `initialHeight` stays 0, which `<vm-bottom-sheet>` treats as "use the default height".
 */
@customElement('vm-video-autosize-bottom-sheet')
export class VmVideoAutosizeBottomSheet extends VmBottomSheet {
    override connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('resize', this._onWindowResize);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('resize', this._onWindowResize);
    }

    override willUpdate(changed: Map<PropertyKey, unknown>): void {
        // Dock ahead of `<vm-bottom-sheet>`'s open handling in `updated()`, which sizes the
        // sheet from `initialHeight` / `fullscreen` and locks the body with `position: fixed`.
        // The scroll has to be committed before that lock.
        if (changed.has('open') && this.open) {
            const { videoWrap } = this._findWraps();
            if (videoWrap) {
                window.scrollTo({ top: videoWrap.getBoundingClientRect().top + window.scrollY, behavior: 'auto' });
            }
            this._measure();
        }
        super.willUpdate(changed);
    }

    private _findWraps() {
        const outerWrap = closestComposed(this, OUTER_WRAP_SELECTOR);
        return {
            videoWrap: outerWrap?.querySelector<HTMLElement>(VIDEO_WRAP_SELECTOR) ?? null,
            actionsWrap: outerWrap?.querySelector<HTMLElement>(ACTIONS_WRAP_SELECTOR) ?? null,
        };
    }

    /**
     * Sizes the sheet so its top edge meets the actions row, assuming the video wrap sits
     * at the top of the viewport. Measured as an offset between the two wraps rather than
     * from the viewport so it holds wherever the page is scrolled.
     */
    private _measure(): void {
        const { videoWrap, actionsWrap } = this._findWraps();
        if (!actionsWrap) {
            this.initialHeight = 0;
            this.fullscreen = false;
            return;
        }

        // No video wrap (e.g. a video still processing) - dock under the actions row where it is.
        const videoTop = videoWrap?.getBoundingClientRect().top ?? 0;
        const height = window.innerHeight - (actionsWrap.getBoundingClientRect().top - videoTop);

        this.fullscreen = height < MIN_SHEET_HEIGHT;
        this.initialHeight = this.fullscreen ? 0 : height;
    }

    // A rotation resizes the player, so re-dock while open. `<vm-bottom-sheet>` animates
    // to a changed `initialHeight` / `fullscreen`.
    private _onWindowResize = (): void => {
        if (!this.open) return;
        this._measure();
    };
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-autosize-bottom-sheet': VmVideoAutosizeBottomSheet;
    }
}
